import { Component, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatchRunService } from '../../../services/match-run.service';
import { PairService } from '../../../services/pair.service';
import { Pair } from '../../../interfaces/pair';
import { interval, Subscription, switchMap, takeWhile } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { MatchingPeriodService } from '../../../services/matchingPeriod.service';
import { Participant } from '../../../interfaces/participant';
import { ParticipantService } from '../../../services/participant.service';
import { AuthService } from '../../../auth/auth.service';
import { Permission } from '../../../interfaces/permission';
import { MatchStartDialogComponent, MatchStartDialogResult } from '../match-start-dialog/match-start-dialog.component';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatchStartErrorDialogComponent } from '../match-start-error-dialog/match-start-error-dialog.component';

@Component({
  selector: 'app-match-start',
  imports: [MatIconModule, MatCardModule, MatButtonModule, MatProgressSpinnerModule, CommonModule],
  templateUrl: './match-start.component.html',
  styleUrl: './match-start.component.scss'
})
export class MatchStartComponent implements OnDestroy {
  pairs: Pair[] = [];
  activePairs: Pair[] = [];
  activeConfirmedPairs: Pair[] = [];
  enisSyncParticipants: Participant[] = [];
  inactivePairs: Pair[] = [];
  loading = false;
  checkedLoading = false;
  statusMessage = 'Processing matchrun...';
  private pollingSubscription: Subscription | null = null;
  currentMatchingPeriodId: string = '';
  latestMatchrun: string = '';
  canStartMatchrun = false;
  participantWarnings: {
    canStartMatchrun: boolean;
    message: string;
    participants: Array<{ id: string; enisNumber: string; type: 'donor' | 'recipient'; warnings: string[] }>;
  } | null = null;

  private readonly SNAPSHOT_KEY = 'matchrun_pairs_snapshot';
  snapshot: { totalPairs: number; activePairs: number; activeConfirmedPairs: number; enisSyncCount: number; inactivePairs: number } | null = null;

  get displayTotalPairs(): number { return this.loading && this.snapshot ? this.snapshot.totalPairs : this.pairs.length; }
  get displayActiveConfirmedPairs(): number { return this.loading && this.snapshot ? this.snapshot.activeConfirmedPairs : this.activeConfirmedPairs.length; }
  get displayEnisSyncCount(): number { return this.loading && this.snapshot ? this.snapshot.enisSyncCount : this.enisSyncParticipants.length; }
  get displayActivePairs(): number { return this.loading && this.snapshot ? this.snapshot.activePairs : this.activePairs.length; }
  get displayInactivePairs(): number { return this.loading && this.snapshot ? this.snapshot.inactivePairs : this.inactivePairs.length; }

  constructor(
    private readonly matchRunService: MatchRunService,
    private readonly pairService: PairService,
    private  participantService: ParticipantService,
    private matchingPeriodService: MatchingPeriodService,
    private authService: AuthService,
    private dialog: MatDialog,
    private router: Router,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit() {
    this.checkPermissions();
    this.getMatchRun();
    this.checkForParticipantWarnings();

    // Check if we navigated here after starting a matchrun
    const navigation = this.router.getCurrentNavigation();
    const matchrunStarted = navigation?.extras?.state?.['matchrunStarted'] ||
                           (history.state && history.state.matchrunStarted);

    if (matchrunStarted) {
      // Matchrun was just started, show loading immediately
      this.loading = true;
      this.checkedLoading = true;
      this.statusMessage = 'Processing matchrun...';
      this.snapshot = this.loadSnapshot();
      if (!this.snapshot) {
        this.fetchPairs();
        this.getAllParticipants();
      }
      this.pollMatchRunStatus();
    } else {
      // Check match run status on page load
      this.matchRunService.getMatchRunStatus().subscribe({
        next: (status) => {
          if (status === 'IN_PROGRESS') {
            this.loading = true;
            this.checkedLoading = true;
            this.statusMessage = 'Processing matchrun...';
            this.snapshot = this.loadSnapshot();
            if (!this.snapshot) {
              this.fetchPairs();
              this.getAllParticipants();
            }
            this.pollMatchRunStatus(); // Start polling if status is "IN_PROGRESS"
          } else {
            this.loading = false; // Show normal screen for other statuses
            this.checkedLoading = true;
            this.fetchPairs();
            this.getAllParticipants();
          }
        },
        error: (err) => {
          console.log(err);
          this.loading = false; // Handle error by showing normal screen
          this.checkedLoading = true;
          this.fetchPairs();
          this.getAllParticipants();
        }
      });
    }
  }

  private fetchPairs(isRetry = false) {
    this.pairService.getAllPairs().subscribe({
      next: (data) => {
        this.pairs = data;
        this.activePairs = this.pairs.filter((pair) => pair.active);
        this.activeConfirmedPairs = this.pairs.filter((pair) => pair.active);
        this.inactivePairs = this.pairs.filter((pair) => !pair.active);
        if (data.length === 0 && !isRetry) {
          setTimeout(() => this.fetchPairs(true), 2000);
        }
      },
      error: (error) => {
        console.log(error);
      }
    });
  }

  private saveSnapshot() {
    const snapshot = {
      totalPairs: this.pairs.length,
      activePairs: this.activePairs.length,
      activeConfirmedPairs: this.activeConfirmedPairs.length,
      enisSyncCount: this.enisSyncParticipants.length,
      inactivePairs: this.inactivePairs.length
    };
    sessionStorage.setItem(this.SNAPSHOT_KEY, JSON.stringify(snapshot));
    this.snapshot = snapshot;
  }

  private loadSnapshot() {
    const stored = sessionStorage.getItem(this.SNAPSHOT_KEY);
    return stored ? JSON.parse(stored) : null;
  }

  private clearSnapshot() {
    sessionStorage.removeItem(this.SNAPSHOT_KEY);
    this.snapshot = null;
  }

  startMatchrun() {
    const dialogRef = this.dialog.open(MatchStartDialogComponent, {
      width: '600px',
      disableClose: true,
      data: {
        isFirstMatchRun: !this.latestMatchrun || this.latestMatchrun === '',
        participantWarnings: this.participantWarnings,
        pairsStatistics: {
          totalPairs: this.pairs.length,
          activeConfirmedPairs: this.activeConfirmedPairs.length,
          enisSyncNeeded: this.enisSyncParticipants.length,
          activePairs: this.activePairs.length,
          inactivePairs: this.inactivePairs.length
        }
      }
    });

    dialogRef.afterClosed().subscribe((result: MatchStartDialogResult) => {
      if (result?.confirmed) {
        this.saveSnapshot();
        this.loading = true;
        this.matchRunService.startMatchrun(result.comment, undefined, result.isMajorMatchrun).subscribe({
          next: () => {
            console.log('Matchrun started');
            this.pollMatchRunStatus();
          },
          error: (err) => {
            console.log(err);
            this.loading = false;
            // Show error message to user
            const errorMessage = err.message || 'Failed to start matchrun. Please try again.';

            // Check if this is an active participants with warnings error
            if (err.error?.code === 'ACTIVE_PARTICIPANTS_WITH_WARNINGS') {
              // Show detailed error dialog for participant warning errors
              this.dialog.open(MatchStartErrorDialogComponent, {
                width: '700px',
                data: {
                  title: 'Cannot Start Matchrun',
                  message: err.error.message,
                  isParticipantWarningError: true,
                  participants: err.error.participants
                }
              });
            }
            // Check if this is a partial lock validation error
            else if (errorMessage.includes('partially locked chains')) {
              // Show detailed error dialog for partial lock errors
              this.dialog.open(MatchStartErrorDialogComponent, {
                width: '600px',
                data: {
                  title: 'Cannot Start Matchrun',
                  message: errorMessage,
                  isPartialLockError: true
                }
              });
            } else {
              // Show generic error notification
              this.showNotification(errorMessage, 'Close');
            }
          }
        });
      }
    });
  }

  pollMatchRunStatus() {
    console.log('poll');
    this.pollingSubscription = interval(60000) // Poll every 60 seconds
      .pipe(
        switchMap(() => this.matchRunService.getMatchRunStatus()), // Call the service
        takeWhile((status) => status === 'IN_PROGRESS', true) // Continue until status is not "in process"
      )
      .subscribe({
        next: (status) => {
          console.log(status);
          if (status !== 'IN_PROGRESS') {
            this.loading = false;
            this.statusMessage = 'Matchrun completed!';
            this.clearSnapshot();
            // Clear cache and notify components BEFORE fetching new data
            this.matchingPeriodService.notifyMatchrunCompleted();
            // Now fetch fresh matchrun data and pairs
            this.getMatchRun();
            this.fetchPairs();
            this.getAllParticipants();
          }
        },
        error: (err) => {
          console.log(err);
          this.loading = false;
        }
      });
  }

  getMatchRun() {
    this.matchingPeriodService.getLatestMatchingPeriod().subscribe((res) => {
      if (res) {
        //Could maybe use ID instead of mPName, both should technically be unique however only the id is actually enforced.
        //mPName does look prettier in the url.
        this.currentMatchingPeriodId = res.matchingPeriodName;
        if (res.matchRuns && res.matchRuns.length > 0) {
          this.latestMatchrun = res.matchRuns[res.matchRuns.length - 1].runNumber.toString();
        }
      }
    });
  }

  getAllParticipants() {
    this.participantService.getAllParticipants().subscribe({
      next: (data) => {
        console.log('Fetched participants:', data);
        this.enisSyncParticipants = data.filter(participant => !participant.firstName && !participant.lastName);
      },
      error: (err) => {
        console.error('Error fetching participants:', err);
      }
    });
  }

  checkPermissions() {
    this.authService.getUser().subscribe({
      next: (user) => {
        this.canStartMatchrun = user.activeRole.permissions.includes(Permission.MATCHRUN_START);
      },
      error: (error) => {
        console.error('Error checking permissions:', error);
        this.canStartMatchrun = false;
      }
    });
  }

  checkForParticipantWarnings() {
    this.matchRunService.checkParticipantWarnings().subscribe({
      next: (result) => {
        this.participantWarnings = result;
        console.log('Participant warnings check:', result);
      },
      error: (error) => {
        console.error('Error checking participant warnings:', error);
        // On error, allow matchrun to proceed (fail open)
        this.participantWarnings = null;
      }
    });
  }

  showWarningDetails() {
    if (this.participantWarnings && this.participantWarnings.participants) {
      // Open the error dialog to show participants with issues
      this.dialog.open(MatchStartErrorDialogComponent, {
        width: '700px',
        data: {
          title: 'Cannot Start Matchrun',
          message: this.participantWarnings.message,
          isParticipantWarningError: true,
          participants: this.participantWarnings.participants
        }
      });
    }
  }

  showNotification(message: string, action: string = 'Close'): void {
    this.snackBar.open(message, action, {
      duration: 5000, // Show for 5 seconds for error messages
      verticalPosition: 'top',
      horizontalPosition: 'center',
      panelClass: ['error-snackbar']
    });
  }

  ngOnDestroy() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }
}
